import { locationApi } from '@/api/account-management/locationApi';
import { providerConfigurationApi } from '@/api/configuration/providerConfigurationApi';
import { OPTIONAL_LOCATION_FIELDS, type Location } from '@/models/account-management/Location';
import { PaymentMethodsAccepted, type Integration } from '@/models/configuration/ProviderConfiguration';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FavoriteIcon from '@mui/icons-material/Favorite';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaymentsIcon from '@mui/icons-material/Payments';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { Controller, useForm, type ControllerFieldState, type ControllerRenderProps } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '../../AppContext';
import { COUNTRIES } from '../../constants/countries';
import { CURRENCIES } from '../../constants/currencies';
import { LOCALES } from '../../constants/locales';
import { CANADIAN_PROVINCES, US_STATES } from '../../constants/states';
import { TIME_ZONES } from '../../constants/timeZones';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { fillHeight } from '../../theme';
import { isValidEmail, isValidPhone } from '../../validation/fieldValidators';
import { deepTrim } from '../../utils/deepTrim';
import BitFlagSelect from '../common/BitFlagSelect';
import SaveFooter from '../common/SaveFooter';
import { usePermissions } from '../../permissions/usePermissions';
import { useSettleDiscard } from '@/hooks/useSettleDiscard';
import { useUnsavedChangesPrompt } from '@/hooks/useUnsavedChangesPrompt';
import { Perm } from '../../permissions/permissions';
import { useToast } from '../common/toast/ToastProvider';
import {
    CASH_TENDER_ID,
    GIFT_CARD_TENDER_ID,
    TENDER_PREFIX,
    TENDER_TOGGLE,
    TOUCHTAB_TENDER_ID,
    visibleTenderIds,
} from './tenderVisibility';

// Section config: prefix -> label + icon. General = the unprefixed (location-level) paths.
const SECTIONS: { key: string; prefix: string | null; label: string; icon: ReactNode }[] = [
    { key: 'General', prefix: null, label: 'Details', icon: <InfoOutlinedIcon color="action" /> },
    { key: 'Payment', prefix: 'paymentConfiguration.', label: 'Payment', icon: <PaymentsIcon color="action" /> },
    { key: 'Pos', prefix: 'posConfiguration.', label: 'POS', icon: <PointOfSaleIcon color="action" /> },
    // Loyalty is optional: the backend only emits loyaltyConfiguration.* schema keys when a loyalty vendor
    // is selected + saved, so this section self-hides (paths.length === 0) when no loyalty is configured.
    { key: 'Loyalty', prefix: 'loyaltyConfiguration.', label: 'Loyalty', icon: <FavoriteIcon color="action" /> },
];

// Provider configs whose configurationJson string is parsed into an object so vendor keys bind by path.
const CONFIG_KEYS = ['posConfiguration', 'paymentConfiguration', 'loyaltyConfiguration'] as const;

// Location identity fields that must never render as editable onboarding inputs even if the schema
// still lists them. Name is shown read-only in the page title, so it's excluded here.
const EXCLUDED_PATHS = new Set<string>(['name']);

// Fields the backend readiness check treats as optional. They are still shown and editable, but they
// must not count toward progress — otherwise a client can never reach 100% (and the location never
// leaves the onboarding list) without filling fields that are not actually required.
const OPTIONAL_PATHS = new Set<string>(OPTIONAL_LOCATION_FIELDS);

// Card-brand tender ids: shown only when the TouchTab tender toggle is OFF. Gift Card and Cash always show.
const CARD_TENDER_KEYS = ['visaTenderId', 'mastercardTenderId', 'amexTenderId', 'discoverTenderId', 'dinersTenderId', 'jcbTenderId'];
// Card-brand tender id -> the payment method that makes it required (per-brand mode only).
const CARD_TENDER_METHODS = [
    { path: `${TENDER_PREFIX}visaTenderId`, flag: PaymentMethodsAccepted.Visa },
    { path: `${TENDER_PREFIX}mastercardTenderId`, flag: PaymentMethodsAccepted.Mastercard },
    { path: `${TENDER_PREFIX}amexTenderId`, flag: PaymentMethodsAccepted.Amex },
    { path: `${TENDER_PREFIX}discoverTenderId`, flag: PaymentMethodsAccepted.Discover },
    { path: `${TENDER_PREFIX}dinersTenderId`, flag: PaymentMethodsAccepted.Diners },
    { path: `${TENDER_PREFIX}jcbTenderId`, flag: PaymentMethodsAccepted.JCB },
];

// Explicit POS non-tender field order in the onboarding form. loyaltyItemId only shows when a loyalty
// vendor is configured; any other POS fields (e.g. vendor config keys) follow after, in schema order.
// locationExternalId is an Omnivore vendor config key (only in the schema for Omnivore) — show it first,
// right under the read-only POS Vendor field.
const POS_LOCATION_EXTERNAL_ID = 'posConfiguration.configurationJson.locationExternalId';
const POS_LOYALTY_ITEM_ID = 'posConfiguration.loyaltyItemId';
const POS_FIELD_ORDER = [POS_LOCATION_EXTERNAL_ID, POS_LOYALTY_ITEM_ID, 'posConfiguration.modifierPriceIncludedInItemPrice'];

// Payment layout: column 1 = vendor + vendor-specific config keys (storeId/terminalId/...); column 2 =
// the typed payment fields in this order.
const PAYMENT_CONFIG_PREFIX = 'paymentConfiguration.configurationJson.';
// Integration-aware vendors namespace each enabled integration's client keys under this prefix, indexed by the
// integration's position in the location's integrationConfigurations list (see the backend schema builder).
const PAYMENT_INTEGRATIONS_PREFIX = 'paymentConfiguration.integrationConfigurations.';
// The shared-credentials pseudo-integration renders first in the payment section (above the specific integrations).
const SHARED_INTEGRATION_KEY = 'shared';
const PAYMENT_FIELD_ORDER = ['paymentConfiguration.paymentMethodsAccepted', 'paymentConfiguration.allowGiftCardTipping'];
const ALLOW_GIFT_CARD_TIPPING = 'paymentConfiguration.allowGiftCardTipping';

// The integration index embedded in a namespaced payment path (…integrationConfigurations.{i}.configurationJson.…).
const paymentIntegrationIndex = (path: string): number | null => {
    if (!path.startsWith(PAYMENT_INTEGRATIONS_PREFIX)) return null;
    const idx = Number(path.slice(PAYMENT_INTEGRATIONS_PREFIX.length).split('.')[0]);
    return Number.isInteger(idx) ? idx : null;
};

type SelectOption = { value: string; label: string };
const COUNTRY_OPTIONS: SelectOption[] = COUNTRIES.map((c) => ({ value: c.code, label: c.name }));
const TIMEZONE_OPTIONS: SelectOption[] = TIME_ZONES.map((t) => ({ value: t.id, label: t.name }));
const CURRENCY_OPTIONS: SelectOption[] = CURRENCIES.map((c) => ({ value: c.code, label: c.name }));
const LOCALE_OPTIONS: SelectOption[] = LOCALES.map((l) => ({ value: l.code, label: l.name }));
const US_STATE_OPTIONS: SelectOption[] = US_STATES.map((s) => ({ value: s.code, label: s.name }));
const CA_PROVINCE_OPTIONS: SelectOption[] = CANADIAN_PROVINCES.map((s) => ({ value: s.code, label: s.name }));

// camelCase last path segment -> "Title Case" label (fallback for fields without a dedicated control/label).
const prettify = (path: string): string => {
    const seg = path.split('.').pop() ?? path;
    const spaced = seg.replace(/([A-Z])/g, ' $1').replace(/[_-]/g, ' ');
    return (spaced.charAt(0).toUpperCase() + spaced.slice(1)).trim();
};

const getAtPath = (obj: unknown, path: string): unknown =>
    path.split('.').reduce<unknown>((acc, seg) => {
        if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[seg];
        return undefined;
    }, obj);

// Matches the backend "filled" rule: string non-empty, number non-zero, bool always answered.
const isFilled = (v: unknown): boolean => {
    if (v == null) return false;
    if (typeof v === 'string') return v.trim().length > 0;
    if (typeof v === 'number') return v !== 0;
    if (typeof v === 'boolean') return true;
    return true;
};

// Turn the loaded Location into form values, parsing each configurationJson string into an object.
const buildFormValues = (loc: Location): Record<string, unknown> => {
    // Location is an interface (no index signature), so it needs to go through unknown to become a
    // bag of dot-path-addressable values.
    const clone = structuredClone(loc) as unknown as Record<string, unknown>;
    for (const key of CONFIG_KEYS) {
        const cfg = clone[key] as Record<string, unknown> | null | undefined;
        if (cfg && typeof cfg.configurationJson === 'string') {
            try { cfg.configurationJson = cfg.configurationJson ? JSON.parse(cfg.configurationJson) : {}; }
            catch { cfg.configurationJson = {}; }
        }
        // Payment integration-aware vendors carry a per-integration list; parse each entry's configurationJson
        // so its namespaced keys bind by path (…integrationConfigurations.{i}.configurationJson.{key}).
        if (key === 'paymentConfiguration' && cfg) parseIntegrationConfigs(cfg, JSON.parse);
    }
    return clone;
};

// Apply a JSON transform (parse or stringify) to each payment integration entry's configurationJson.
const parseIntegrationConfigs = (paymentCfg: Record<string, unknown>, transform: (s: string) => unknown): void => {
    const integs = paymentCfg.integrationConfigurations as Array<Record<string, unknown>> | undefined;
    if (!Array.isArray(integs)) return;
    for (const ic of integs) {
        if (ic && typeof ic.configurationJson === 'string') {
            try { ic.configurationJson = ic.configurationJson ? transform(ic.configurationJson) : {}; }
            catch { ic.configurationJson = {}; }
        }
    }
};

// Stringify each payment integration entry's configurationJson object back to a JSON string (save direction).
const stringifyIntegrationConfigs = (paymentCfg: Record<string, unknown>): void => {
    const integs = paymentCfg.integrationConfigurations as Array<Record<string, unknown>> | undefined;
    if (!Array.isArray(integs)) return;
    for (const ic of integs) {
        if (ic && typeof ic.configurationJson === 'object') ic.configurationJson = JSON.stringify(ic.configurationJson ?? {});
    }
};

// Per-location client onboarding form: the client-onboardable fields grouped into accordion sections,
// each header showing an icon and a completed/total progress chip.
const OnboardingForm = (): ReactElement => {
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const canUpdate = usePermissions().canUpdate(Perm.configurationOnboarding);
    const navigate = useNavigate();
    const toast = useToast();
    const { slug } = useParams();
    const queryClient = useQueryClient();

    const [initValues, setInitValues] = useState<Record<string, unknown> | null>(null);
    const [saving, setSaving] = useState(false);
    const [showCompleteDialog, setShowCompleteDialog] = useState(false);

    const { data: location } = useQuery<Location>({
        queryKey: ['location', slug ?? ''],
        enabled: !!slug,
        queryFn: () => locationApi.getBySlug(slug!),
        // Match LocationDetails (same ['location', slug] cache): always load fresh on mount so saved
        // onboarding changes show immediately instead of serving an indefinitely-cached copy.
        staleTime: 0,
        refetchOnMount: 'always',
        // Not on focus or reconnect, matching LocationDetails and BrandDetails. A refetch replaces `location`,
        // which re-runs the effect below and calls reset() — so alt-tabbing away and back silently threw away
        // whatever the client had typed. It also defeats the unsaved-changes guard added here, because the
        // form goes clean before anything can ask about it (TTD-163).
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const { data: schema } = useQuery<string[]>({
        // The schema is the SELECTED vendors' onboardable fields, so it must refetch when a vendor changes —
        // key by the POS/Payment vendors (and always refetch on mount) so a vendor switch shows the new
        // vendor's fields instead of serving the previous vendor's (e.g. GlobalPay's merchantId lingering
        // after switching to ParPay).
        queryKey: ['onboarding-schema', location?.id ?? '', location?.posConfiguration?.vendor ?? '', location?.paymentConfiguration?.vendor ?? '', location?.loyaltyConfiguration?.vendor ?? ''],
        enabled: !!location?.id,
        queryFn: () => locationApi.getOnboardingSchema(location!.id!),
        staleTime: 0,
        refetchOnMount: 'always',
    });

    // The payment vendor's integrations, so per-integration onboarding groups can show their display names.
    const paymentVendor = location?.paymentConfiguration?.vendor ?? '';
    const { data: paymentIntegrations } = useQuery<Integration[]>({
        queryKey: ['integrations', 1, paymentVendor],
        enabled: !!paymentVendor,
        queryFn: () => providerConfigurationApi.listIntegrations(1, paymentVendor),
        staleTime: Infinity,
        gcTime: Infinity,
    });

    const methods = useForm();
    const { control, reset, getValues, watch, setValue, handleSubmit, formState } = methods;
    const settleDiscard = useSettleDiscard(methods);

    // Warn before leaving with unsaved edits. This screen was never wired up, so a client could fill in a
    // page of onboarding, click a menu item, and lose all of it without being asked (TTD-163).
    //
    // The second argument is how it leaves itself when the answer is given somewhere the router cannot see —
    // switching org/brand/location. Back to the onboarding list, which is where Cancel goes too.
    const leaveToOnboardingList = useCallback(
        () => navigate(`/${app.organizationSlug}/configuration/onboarding`),
        [navigate, app.organizationSlug]
    );
    useUnsavedChangesPrompt(formState.isDirty, leaveToOnboardingList);

    useEffect(() => {
        if (location) {
            const values = buildFormValues(location);
            setInitValues(values);
            reset(values);
        }
    }, [location, reset]);

    const sections = useMemo(() => {
        if (!schema) return [];
        return SECTIONS
            .map((s) => ({
                ...s,
                paths: schema.filter((p) => !EXCLUDED_PATHS.has(p) && (s.prefix == null ? !p.includes('.') : p.startsWith(s.prefix))),
            }))
            .filter((s) => s.paths.length > 0);
    }, [schema]);

    // Re-read on every change so the progress chips update live.
    const values = watch();

    const paymentMethodOptions = [
        { value: PaymentMethodsAccepted.Visa, label: strings.visa },
        { value: PaymentMethodsAccepted.Mastercard, label: strings.masterCard },
        { value: PaymentMethodsAccepted.Amex, label: strings.amex },
        { value: PaymentMethodsAccepted.Discover, label: strings.discover },
        { value: PaymentMethodsAccepted.RoomCharge, label: strings.roomCharge },
        { value: PaymentMethodsAccepted.Diners, label: strings.dinersClub },
        { value: PaymentMethodsAccepted.JCB, label: strings.jcb },
        { value: PaymentMethodsAccepted.GiftCard, label: strings.giftCard },
    ];

    // Phone/email are optional, so only check the format once something has actually been entered.
    const rulesFor = (path: string) => {
        if (path === 'email') return { validate: (v?: string) => !v?.trim() || isValidEmail(v) || strings.validationEmailInvalid };
        if (path === 'phoneNumber') return { validate: (v?: string) => !v?.trim() || isValidPhone(v) || strings.validationPhoneNumberInvalid };
        return undefined;
    };

    const renderSelect = (field: ControllerRenderProps, label: string, options: SelectOption[], required: boolean): ReactElement => (
        <TextField select label={label} size="small" required={required} sx={{ width: 360 }} value={field.value ?? ''} onChange={field.onChange}>
            {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
    );

    // `required` here mirrors "counts toward the progress chip" — the two are driven by the same set, so
    // the asterisks and the x/y count can never disagree. It marks the field, it does not block saving:
    // onboarding is filled in incrementally.
    const renderControl = (path: string, field: ControllerRenderProps, fieldState: ControllerFieldState, required: boolean): ReactNode => {
        const error = fieldState.error?.message;

        switch (path) {
            case 'country': return renderSelect(field, strings.country, COUNTRY_OPTIONS, required);
            case 'timeZoneId': return renderSelect(field, strings.timeZone, TIMEZONE_OPTIONS, required);
            case 'currency': return renderSelect(field, strings.currency, CURRENCY_OPTIONS, required);
            case 'locale': return renderSelect(field, strings.locale, LOCALE_OPTIONS, required);
            case 'state': {
                const country = getAtPath(values, 'country');
                if (country === 'US') return renderSelect(field, strings.state, US_STATE_OPTIONS, required);
                if (country === 'CA') return renderSelect(field, strings.province, CA_PROVINCE_OPTIONS, required);
                break; // other countries -> free text (generic fallback below)
            }
            case 'paymentConfiguration.paymentMethodsAccepted':
                return (
                    <FormControl required={required} sx={{ width: 360 }}>
                        <InputLabel id="pma-label">{strings.paymentMethodsAccepted}</InputLabel>
                        <BitFlagSelect
                            labelId="pma-label"
                            label={strings.paymentMethodsAccepted}
                            value={typeof field.value === 'number' ? field.value : 0}
                            onChange={field.onChange}
                            options={paymentMethodOptions}
                        />
                    </FormControl>
                );
            case TENDER_TOGGLE:
                return (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!field.value}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    field.onChange(checked);
                                    // Clear the now-hidden tender ids so stale values aren't saved (mirrors ProviderConfig).
                                    if (checked) CARD_TENDER_KEYS.forEach((k) => setValue(`${TENDER_PREFIX}${k}`, '', { shouldDirty: true }));
                                    else setValue(TOUCHTAB_TENDER_ID, '', { shouldDirty: true });
                                }}
                            />
                        }
                        label={strings.useTouchTabTenderType}
                    />
                );
        }

        // Generic fallback by value type. Booleans are always "answered", so they're never required.
        const v = getAtPath(initValues, path);
        if (typeof v === 'boolean') {
            return <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label={prettify(path)} />;
        }
        if (typeof v === 'number') {
            // Show 0 (the "not filled" sentinel for numbers) as an empty field: otherwise the default "0"
            // can't be cleared (clearing maps back to 0) and typing an id next to it yields a leading zero.
            return <TextField label={prettify(path)} type="number" size="small" required={required} sx={{ width: 360 }} value={field.value ? field.value : ''} onChange={(e) => field.onChange(e.target.value === '' ? 0 : Number(e.target.value))} error={!!error} helperText={error} />;
        }
        // Phone/email get their hint even without an error; other free-text fields don't.
        const hint = error ?? (path === 'phoneNumber' ? strings.phoneNumberHint : undefined);
        return <TextField label={prettify(path)} size="small" required={required} sx={{ width: 360 }} value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} helperText={hint} />;
    };

    // Render a Controller-bound input for each schema path. `requiredPaths` is the section's counted set,
    // so the required (*) marker and the x/y progress chip are driven by exactly the same thing — including
    // the tender fields, where "required" depends on the accepted payment methods.
    const renderFields = (paths: string[], requiredPaths: Set<string>): ReactElement[] =>
        paths.map((path) => (
            <Controller
                key={path}
                name={path}
                control={control}
                rules={rulesFor(path)}
                render={({ field, fieldState }) => <>{renderControl(path, field, fieldState, requiredPaths.has(path))}</>}
            />
        ));

    const handleSave = async () => {
        // The form only renders once the location has loaded (guard below), but narrow for the compiler
        // and capture the id so it stays non-null across the awaits.
        if (!location?.id) return;
        const locationId = location.id;

        setSaving(true);
        try {
            // Trim before stringifying the config JSON so vendor field values (and every other field) are
            // normalized on the way to the server.
            const payload = deepTrim(structuredClone(getValues())) as Record<string, unknown>;
            for (const key of CONFIG_KEYS) {
                const cfg = payload[key] as Record<string, unknown> | null | undefined;
                if (cfg && typeof cfg.configurationJson === 'object') {
                    cfg.configurationJson = JSON.stringify(cfg.configurationJson ?? {});
                }
                if (key === 'paymentConfiguration' && cfg) stringifyIntegrationConfigs(cfg);
            }
            await locationApi.applyOnboarding(locationId, payload as unknown as Location);
            // Clear the dirty flag now and refresh this location + the pending grid.
            reset(getValues());
            queryClient.invalidateQueries({ queryKey: ['location', slug] });
            queryClient.invalidateQueries({ queryKey: ['onboarding-pending'] });

            // The backend drops a location from the pending list once every required field is filled. If this
            // save completed the client's onboarding, congratulate them and send them back to the list;
            // otherwise it's an in-place save. A failure of this check must not read as a save error — the
            // save already succeeded — so fall back to the in-place success toast.
            let complete = false;
            try {
                const pending = await locationApi.getOnboardingPending(location.brandId);
                complete = !pending.some((p) => p.id === locationId);
            } catch { /* fall back to the in-place success toast below */ }

            if (complete) {
                setShowCompleteDialog(true);
            } else {
                toast({ severity: 'success', message: strings.locationSaveSuccess });
            }
        } catch {
            toast({ severity: 'error', message: strings.locationSaveError });
        } finally {
            setSaving(false);
        }
    };

    // Onboarding-complete acknowledgement: return to the onboarding list and refresh it.
    const handleCompleteOk = () => {
        setShowCompleteDialog(false);
        queryClient.invalidateQueries({ queryKey: ['onboarding-pending'] });
        navigate(`/${app.organizationSlug}/configuration/onboarding`);
    };

    if (!location || !initValues) {
        return <Box sx={{ p: 2 }}><Typography color="text.secondary">{strings.loading}</Typography></Box>;
    }

    return (
        <Box sx={{ ...fillHeight, overflow: 'auto', p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>{location.name}</Typography>

            {/* noValidate: the required (*) markers are informational; onboarding is filled incrementally.
                react-hook-form still enforces the phone/email format rules. */}
            <form noValidate id="onboarding-form" onSubmit={handleSubmit(handleSave)}>
            {sections.map((section) => {
                // POS tenders are grouped under a "Tenders" header and conditionally shown by the toggle.
                const isPos = section.key === 'Pos';
                const useTouchTab = isPos && !!getAtPath(values, TENDER_TOGGLE);
                // Read up here because it now decides VISIBILITY as well as requiredness: cash follows its
                // accepted payment method, exactly as gift card does for the count below.
                const acceptedMethods = isPos ? (Number(getAtPath(values, 'paymentConfiguration.paymentMethodsAccepted')) || 0) : 0;
                const tenderPaths = isPos ? section.paths.filter((p) => p.startsWith(TENDER_PREFIX)) : [];
                const nonTenderPaths = isPos ? section.paths.filter((p) => !p.startsWith(TENDER_PREFIX)) : section.paths;
                // Order the POS non-tender fields explicitly; hide loyaltyItemId unless a loyalty vendor is set.
                const loyaltyVendorSet = !!location.loyaltyConfiguration?.vendor;
                const orderedNonTender = isPos
                    ? [
                        ...POS_FIELD_ORDER.filter((p) => nonTenderPaths.includes(p) && (p !== POS_LOYALTY_ITEM_ID || loyaltyVendorSet)),
                        ...nonTenderPaths.filter((p) => !POS_FIELD_ORDER.includes(p)),
                    ]
                    : nonTenderPaths;
                // Visible tender ids: Gift Card always; Cash when accepted; TouchTab id only when on;
                // card-brand ids only when off.
                const activeTenderIds = visibleTenderIds(tenderPaths, useTouchTab, acceptedMethods);
                // Required tender ids for the count (independent of what's shown): Gift Card and Cash whenever
                // they are accepted methods (both modes); the TouchTab id in TouchTab mode, else one per
                // accepted card brand.
                const giftCardRequired = (acceptedMethods & PaymentMethodsAccepted.GiftCard) !== 0 && tenderPaths.includes(GIFT_CARD_TENDER_ID);
                const cashRequired = (acceptedMethods & PaymentMethodsAccepted.Cash) !== 0 && tenderPaths.includes(CASH_TENDER_ID);
                const requiredTenderIds = useTouchTab
                    ? [...(tenderPaths.includes(TOUCHTAB_TENDER_ID) ? [TOUCHTAB_TENDER_ID] : []), ...(giftCardRequired ? [GIFT_CARD_TENDER_ID] : [])]
                    : [...CARD_TENDER_METHODS.filter((m) => (acceptedMethods & m.flag) !== 0 && tenderPaths.includes(m.path)).map((m) => m.path), ...(giftCardRequired ? [GIFT_CARD_TENDER_ID] : [])];
                if (cashRequired) requiredTenderIds.push(CASH_TENDER_ID);
                // Progress counts exactly the required fields (kept in lockstep with the asterisks). Excludes
                // booleans (always answered) and the explicitly-optional fields (phone/email/address2), so a
                // location can actually reach 100% and leave the onboarding list.
                const countPaths = (isPos ? [...orderedNonTender, ...requiredTenderIds] : section.paths)
                    .filter((p) => typeof getAtPath(initValues, p) !== 'boolean' && !OPTIONAL_PATHS.has(p));
                const filled = countPaths.filter((p) => isFilled(getAtPath(values, p))).length;
                const total = countPaths.length;
                const complete = total > 0 && filled === total;
                // Same set drives the required (*) markers, so asterisks and the count never disagree.
                const requiredPaths = new Set(countPaths);
                // General is split into two columns: col 1 = through Country, col 2 = TimeZone onward.
                const tzIdx = section.prefix == null ? section.paths.indexOf('timeZoneId') : -1;
                const twoColumn = tzIdx > 0;
                // Read-only vendor field shown at the top of POS/Payment/Loyalty (client sees it, can't edit it).
                const vendorField = (section.key === 'Pos' || section.key === 'Payment' || section.key === 'Loyalty') ? (
                    <TextField
                        label={`${section.label} ${strings.vendor}`}
                        size="small"
                        sx={{ width: 360 }}
                        value={(section.key === 'Pos'
                            ? location.posConfiguration?.vendor
                            : section.key === 'Payment'
                                ? location.paymentConfiguration?.vendor
                                : location.loyaltyConfiguration?.vendor) ?? ''}
                        // `disabled`, not `InputProps.readOnly`. readOnly stops typing but leaves the field
                        // looking exactly like the editable ones beside it, so a client cannot tell which
                        // fields are theirs to fill in (TTD-162). Safe here because this is a display-only
                        // field — it is not registered with the form, so nothing depends on it being
                        // submitted.
                        disabled
                    />
                ) : null;
                // Payment: column 1 = vendor + vendor-specific config keys; column 2 = typed payment fields.
                const isPayment = section.key === 'Payment';
                // Legacy single-integration vendor keys (non-integration-aware vendors keep this flat prefix).
                const paymentVendorKeys = isPayment ? section.paths.filter((p) => p.startsWith(PAYMENT_CONFIG_PREFIX)) : [];
                // Integration-aware vendors: group each enabled integration's namespaced keys by its index, in
                // order, so each renders under its own display-name heading.
                const paymentIntegrationGroups: { index: number; label: string; paths: string[] }[] = [];
                if (isPayment) {
                    const byIndex = new Map<number, string[]>();
                    for (const p of section.paths) {
                        const idx = paymentIntegrationIndex(p);
                        if (idx == null) continue;
                        (byIndex.get(idx) ?? byIndex.set(idx, []).get(idx)!).push(p);
                    }
                    const integConfigs = location.paymentConfiguration?.integrationConfigurations ?? [];
                    // Shared credentials render first (above the specific integrations); the rest keep their order.
                    const typeAt = (i: number) => integConfigs[i]?.integrationType ?? '';
                    const entries = [...byIndex.entries()].sort((a, b) => {
                        const aShared = typeAt(a[0]) === SHARED_INTEGRATION_KEY;
                        const bShared = typeAt(b[0]) === SHARED_INTEGRATION_KEY;
                        if (aShared !== bShared) return aShared ? -1 : 1;
                        return a[0] - b[0];
                    });
                    for (const [index, paths] of entries) {
                        const type = typeAt(index);
                        const label = paymentIntegrations?.find((i) => i.key === type)?.displayName || prettify(`_.${type}`);
                        paymentIntegrationGroups.push({ index, label, paths });
                    }
                }
                // Allow Gift Card Tipping only applies when Gift Card is an accepted payment method.
                const giftCardAccepted = isPayment && ((Number(getAtPath(values, 'paymentConfiguration.paymentMethodsAccepted')) || 0) & PaymentMethodsAccepted.GiftCard) !== 0;
                const paymentTyped = isPayment
                    ? section.paths.filter((p) => !p.startsWith(PAYMENT_CONFIG_PREFIX) && !p.startsWith(PAYMENT_INTEGRATIONS_PREFIX) && (p !== ALLOW_GIFT_CARD_TIPPING || giftCardAccepted))
                    : [];
                const paymentTypedOrdered = [
                    ...PAYMENT_FIELD_ORDER.filter((p) => paymentTyped.includes(p)),
                    ...paymentTyped.filter((p) => !PAYMENT_FIELD_ORDER.includes(p)),
                ];
                return (
                    <Accordion key={section.key} defaultExpanded={!complete}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                                {section.icon}
                                <Typography sx={{ flex: 1 }}>{section.label}</Typography>
                                <Chip
                                    size="small"
                                    color={complete ? 'success' : 'warning'}
                                    variant={complete ? 'filled' : 'outlined'}
                                    label={`${filled}/${total}`}
                                />
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ bgcolor: 'common.white' }}>
                            {twoColumn ? (
                                <Stack direction="row" spacing={6} alignItems="flex-start" sx={{ pt: 1 }}>
                                    <Stack spacing={2}>{renderFields(section.paths.slice(0, tzIdx), requiredPaths)}</Stack>
                                    <Stack spacing={2}>{renderFields(section.paths.slice(tzIdx), requiredPaths)}</Stack>
                                </Stack>
                            ) : isPos ? (
                                // POS: column 1 = vendor + non-tender fields, column 2 = the Tenders group.
                                <Stack direction="row" spacing={6} alignItems="flex-start" sx={{ pt: 1 }}>
                                    <Stack spacing={2}>
                                        {vendorField}
                                        {renderFields(orderedNonTender, requiredPaths)}
                                    </Stack>
                                    {tenderPaths.length > 0 && (
                                        <Stack spacing={2}>
                                            <Typography variant="h6">Tenders</Typography>
                                            {renderFields([TENDER_TOGGLE, ...activeTenderIds], requiredPaths)}
                                        </Stack>
                                    )}
                                </Stack>
                            ) : isPayment ? (
                                // Payment: top row = vendor + shared typed fields (+ any legacy flat vendor keys);
                                // below, one labeled group per enabled integration with its own client fields.
                                <Stack spacing={3} sx={{ pt: 1 }}>
                                    <Stack direction="row" spacing={6} alignItems="flex-start">
                                        <Stack spacing={2}>
                                            {vendorField}
                                            {renderFields(paymentVendorKeys, requiredPaths)}
                                        </Stack>
                                        {paymentTypedOrdered.length > 0 && (
                                            <Stack spacing={2}>{renderFields(paymentTypedOrdered, requiredPaths)}</Stack>
                                        )}
                                    </Stack>
                                    {paymentIntegrationGroups.map((g) => (
                                        <Stack key={g.index} spacing={2}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{g.label}</Typography>
                                            {renderFields(g.paths, requiredPaths)}
                                        </Stack>
                                    ))}
                                </Stack>
                            ) : (
                                <Stack spacing={2} sx={{ pt: 1 }}>
                                    {vendorField}
                                    {renderFields(orderedNonTender, requiredPaths)}
                                </Stack>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
            </form>

            <SaveFooter
                readOnly={!canUpdate}
                formId="onboarding-form"
                formIsDirty={formState.isDirty}
                saving={saving}
                saveButtonTitle={strings.save}
                cancelButtonTitle={strings.cancel}
                onCancel={() => navigate(`/${app.organizationSlug}/configuration/onboarding`)}
                // Cancel puts the form back and stays; Close (shown once clean) is what leaves (TTD-161).
                // initValues is what the location was last loaded/saved as, which is what reset() needs.
                onDiscard={() => { if (initValues) reset(initValues); settleDiscard(); }}
            />

            <Dialog
                open={showCompleteDialog}
                onClose={handleCompleteOk}
                aria-labelledby="onboarding-complete-title"
                aria-describedby="onboarding-complete-description"
            >
                <DialogTitle id="onboarding-complete-title" sx={{ fontWeight: 700 }}>{strings.onboardingCompleteTitle}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="onboarding-complete-description">{strings.onboardingCompleteMessage}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCompleteOk} autoFocus>{strings.ok}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OnboardingForm;
